'use client'

import { useHandleTopCityBookNow } from '@/src/utils/navigation/handleTopCityBookNow'
import { useHomeSearchPrefill } from '@/src/utils/navigation/useHomeSearchPrefill'
import { useTranslations } from 'next-intl'
import React, { useCallback } from 'react'

import { TopCitiesAndRoutes } from './topCitiesAndRoutes'

export interface TopCitiesTransformedData {
    title: string
    routes: Array<{
        id: string
        destination: string
        imageUrl: string
        routes: Array<{
            origin: string
            destination: string
            href: string
            fromCode?: string
            toCode?: string
        }>
    }>
    familyTrips: {
        bannerImage: string
        title: string
        subtitle: string
        subtitleSuffix: string
        overlayTitle: string
        overlayDescription: string
        bookNowHref: string
        destinations: Array<{
            city: string
            country: string
            href: string
        }>
    }
}

interface TopCitiesAndRoutesWrapperProps {
    serverData: TopCitiesTransformedData | null
}

/**
 * Client wrapper for Top Cities and Routes.
 * Receives server-fetched data, provides handlers for Book Now and prefill.
 */
export const TopCitiesAndRoutesWrapper: React.FC<TopCitiesAndRoutesWrapperProps> = ({ serverData }) => {
    const t = useTranslations('homePage.headings')
    const { handleBookNow } = useHandleTopCityBookNow()
    const { handlePrefillFromHref } = useHomeSearchPrefill({
        onBothParams: (from, to) => handleBookNow(from, to)
    })

    if (!serverData?.routes?.length) return null

    const handleBookClick = useCallback(
        (href: string, origin?: string, destination?: string, fromCode?: string, toCode?: string) => {
            if (origin && destination) {
                handleBookNow(origin, destination, fromCode, toCode)
                return
            }
            if (handlePrefillFromHref(href)) {
                return
            }
            window.open(href, '_blank')
        },
        [handleBookNow, handlePrefillFromHref]
    )

    return (
        <TopCitiesAndRoutes
            title={serverData.title}
            routes={serverData.routes}
            familyTrips={serverData.familyTrips}
            onBookClick={handleBookClick}
            onPrefillFromHref={handlePrefillFromHref}
            t={t}
        />
    )
}

export default TopCitiesAndRoutesWrapper
